import { ATTACHMENT_KIND, SHEET_SECTION, isSheetSectionEnabled } from './sheet-sections.js';
export const PREFILL_PART = {
    /** 01 主信息里设计师录入的部分：针距、供应商、版型模块号、模块委托编号/款号；基准尺码两款都有才带 */
    DESIGN_BASIC: 'DESIGN_BASIC',
    DESIGN_MEMO: 'DESIGN_MEMO',
    /** 图示图稿 / 详细图片 / 成品实物图 */
    DESIGN_IMAGES: 'DESIGN_IMAGES',
    MATERIALS: 'MATERIALS',
    COLORWAYS: 'COLORWAYS',
    /** 辅料 + 缝纫线，连同每行的辅材图片 */
    SUB_MATERIALS: 'SUB_MATERIALS',
    /** 标签缝制方法、Shipping 四个分区的图片 */
    MATERIAL_IMAGES: 'MATERIAL_IMAGES',
    /** 基准尺寸，连同版型规格图 */
    STD_SIZES: 'STD_SIZES',
    ONLINE: 'ONLINE',
    USP: 'USP',
};
/** 按画面顺序排列；section 决定预填记到哪一节的修改履历 */
export const PREFILL_PARTS = [
    { part: PREFILL_PART.DESIGN_BASIC, section: SHEET_SECTION.DESIGN },
    { part: PREFILL_PART.DESIGN_MEMO, section: SHEET_SECTION.DESIGN },
    { part: PREFILL_PART.DESIGN_IMAGES, section: SHEET_SECTION.DESIGN },
    { part: PREFILL_PART.MATERIALS, section: SHEET_SECTION.MATERIAL },
    { part: PREFILL_PART.COLORWAYS, section: SHEET_SECTION.MATERIAL },
    { part: PREFILL_PART.SUB_MATERIALS, section: SHEET_SECTION.MATERIAL },
    { part: PREFILL_PART.MATERIAL_IMAGES, section: SHEET_SECTION.MATERIAL },
    { part: PREFILL_PART.STD_SIZES, section: SHEET_SECTION.PATTERN },
    { part: PREFILL_PART.ONLINE, section: SHEET_SECTION.ONLINE },
    { part: PREFILL_PART.USP, section: SHEET_SECTION.USP },
];
export const ENABLED_PREFILL_PARTS = PREFILL_PARTS.filter((p) => isSheetSectionEnabled(p.section));
export function isPrefillPartEnabled(part) {
    return ENABLED_PREFILL_PARTS.some((p) => p.part === part);
}
/**
 * 各部分连带复制的附件用途。
 * 不带的：QC 附件（每轮样品各不相同）。04 数量、05 Tech Pack、06 QC 整节都不预填 ——
 * 数量是 SAP 的只读数据，Tech Pack 是渲染结果，QC 是每个样品自己的检验记录。
 */
export const PREFILL_ATTACHMENT_KINDS = {
    [PREFILL_PART.DESIGN_IMAGES]: [
        ATTACHMENT_KIND.FLAT_SKETCH,
        ATTACHMENT_KIND.DETAIL_IMAGE,
        ATTACHMENT_KIND.FG_IMAGE,
    ],
    [PREFILL_PART.SUB_MATERIALS]: [ATTACHMENT_KIND.SUB_MATERIAL_IMAGE],
    [PREFILL_PART.MATERIAL_IMAGES]: [
        ATTACHMENT_KIND.LABEL_SEWING,
        ATTACHMENT_KIND.SHIPPING,
        ATTACHMENT_KIND.SHIP_TAGGING,
        ATTACHMENT_KIND.SHIP_BOXING,
        ATTACHMENT_KIND.SHIP_FOLDING,
        ATTACHMENT_KIND.SHIP_IRON,
    ],
    [PREFILL_PART.STD_SIZES]: [ATTACHMENT_KIND.PATTERN_SPEC_IMAGE],
};
const filled = (v) => v !== null && v !== undefined && v !== '';
const countFilled = (values) => values.filter(filled).length;
/**
 * 预填时清空单价与币种（2026-10-05 用户确认）。
 *
 * 面料与辅料的用量、规格照常带过来（那是款式复用的核心），但**单价不带**：
 * 价格属于面辅料采购的职责域，源单的价格多半是上一款的谈判结果，
 * 静默覆盖到新单上会让人以为是本款的价格。且预填本来就不走编辑域逐列合并
 * （`prefill-plan.ts` 整表写入），不主动清掉就会直接落库。
 *
 * 与 PRICE 编辑域的规则一致（单价归面料采购与生产管理可改，设计师只看），
 * 但这里更彻底：连币种一起清，避免留下「有价格无币种」的半截数据。
 *
 * 用泛型透传原类型，调用点的字段推断不受影响。
 */
export function stripPrefillPrice(row) {
    return { ...row, unitPrice: null, unitPriceCurrency: null };
}
/**
 * 这一部分在单子上有几条实际内容。
 *
 * 新建委托书时预建的空行不算：ColorWay 按款式颜色建的空色行、6 行空 Memo、
 * 一行空的主材料（只标了全色共通）。否则每张新单看起来都「已有内容」，
 * 预填时会被误判成要覆盖。
 */
export function prefillContentCount(sheet, part) {
    const kinds = PREFILL_ATTACHMENT_KINDS[part] ?? [];
    const attachments = sheet.attachments.filter((a) => kinds.includes(a.kind)).length;
    switch (part) {
        case PREFILL_PART.DESIGN_BASIC:
            // 基准尺码不算：建单时就从 SAP 款式主数据带进来了
            return countFilled([
                sheet.stitchDensity,
                sheet.patternModuleNo,
                sheet.moduleModelCd,
                sheet.moduleStyleCd,
            ]);
        case PREFILL_PART.DESIGN_MEMO:
            return sheet.designMemos.filter((m) => filled(m.contentKo) || filled(m.contentEn)).length;
        case PREFILL_PART.MATERIALS:
            return sheet.materials.filter((m) => countFilled([
                m.usedPartCd,
                m.materialGroupCd,
                m.materialCode,
                m.materialNm,
                m.materialAlias,
                m.materialMemo,
                m.colorMatchPartCd,
                m.vendorNm,
                m.composition,
                m.finishing,
                m.width,
                m.yardage,
                m.realYardage,
                m.unitPrice,
                m.weight,
            ]) > 0).length;
        case PREFILL_PART.COLORWAYS:
            return sheet.colorways.filter((c) => filled(c.washingInfo) || Object.values(c.colors).some((x) => filled(x.cd) || filled(x.nm))).length;
        case PREFILL_PART.SUB_MATERIALS:
            return sheet.subMaterials.length;
        case PREFILL_PART.STD_SIZES:
            return sheet.stdSizes.length + attachments;
        case PREFILL_PART.ONLINE: {
            const { sizeUnit: _unit, ...info } = sheet.onlineInfo;
            return countFilled(Object.values(info)) + sheet.onlineSizeSpecs.length;
        }
        case PREFILL_PART.USP:
            return countFilled(Object.values(sheet.usp)) + sheet.uspPoints.length;
        case PREFILL_PART.DESIGN_IMAGES:
        case PREFILL_PART.MATERIAL_IMAGES:
            return attachments;
    }
}
//# sourceMappingURL=prefill.js.map